import { FieldPath, FieldValues, get, useController } from 'react-hook-form';
import { FormTextInputProps, FormTextInput } from '@britishcouncil/react-admin';

import { useFormContext } from './FormContext';
import { ReadonlyFormValue } from './ReadonlyFormValue';
import { RHFormFieldProps } from './types';

type RHFormInputProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = RHFormFieldProps<FormTextInputProps<string>, TFieldValues, TName> & {
  readonlyMode?: boolean;
};

export const RHFormInput = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({
  name,
  control,
  rules,
  shouldUnregister,
  defaultValue,
  label,
  onBlur,
  readonlyMode,
  ...props
}: RHFormInputProps<TFieldValues, TName>) => {
  const { mode } = useFormContext();
  const { field, formState } = useController({
    name,
    control,
    rules,
    defaultValue,
    shouldUnregister,
  });

  if (mode === 'read' || readonlyMode) {
    return (
      <ReadonlyFormValue
        label={label}
        value={field.value && field.value != '' ? field.value : '-'}
      />
    );
  }

  const handleOnChange = (value: string) => {
    if (props.type === 'number') {
      field.onChange(value === '' || Number.isNaN(Number(value)) ? null : Number(value));
      return;
    }
    field.onChange(value);
  };

  const handleOnBlur = () => {
    field.onBlur();
    onBlur?.();
  };

  return (
    <FormTextInput
      {...props}
      label={label}
      value={field.value ?? ''}
      name={field.name}
      onBlur={handleOnBlur}
      onChange={handleOnChange}
      touched
      error={get(formState.errors, field.name)?.message}
    />
  );
};
